import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Mail,
  MessageSquare,
  FileText,
  Package,
  Grid,
  User,
  Calendar,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Eye,
  Star,
  Plus,
  ArrowRight,
  BarChart3,
  Activity,
  Zap,
  Newspaper,
  Bell,
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Shield,
  Award,
  Target,
  Rocket,
  Heart,
  BookOpen,
  ShoppingBag,
  Inbox,
  Send,
  Archive,
  Flag,
  MoreVertical,
} from "lucide-react";
import { dashboardService } from "../../api/services/dashboardService";
import { useNavigate, useLocation } from "react-router-dom";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    admins: { total: 0, active: 0, recentlyActive: 0 },
    requests: {
      total: 0,
      pending: 0,
      completed: 0,
      thisMonth: 0,
      completionRate: 0,
    },
    messages: {
      total: 0,
      unread: 0,
      resolved: 0,
      thisMonth: 0,
      resolvedRate: 0,
    },
    blogs: { total: 0, published: 0, drafts: 0, totalViews: 0 },
    products: { total: 0, active: 0, featured: 0, totalViews: 0 },
    notifications: { total: 0, unread: 0 },
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [greeting, setGreeting] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");

    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(timer);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await dashboardService.getCurrentUser();
      setCurrentUser(user);

      const [statsResponse, activityResponse, metricsResponse] =
        await Promise.all([
          dashboardService.getStats(),
          dashboardService.getRecentActivity(10),
          dashboardService.getPerformanceMetrics(),
        ]);

      if (statsResponse.success) setStats(statsResponse.data);
      if (activityResponse.success) setRecentActivity(activityResponse.data);
      if (metricsResponse.success) setPerformanceMetrics(metricsResponse.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInHours = Math.floor((now - past) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return "Yesterday";
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const getActivityIcon = (type) => {
    const icons = {
      product: Package,
      request: FileText,
      message: MessageSquare,
      blog: BookOpen,
      admin: Users,
    };
    return icons[type] || Activity;
  };

  const getActivityColor = (type) => {
    const colors = {
      product: "bg-pink-500",
      request: "bg-amber-500",
      message: "bg-emerald-500",
      blog: "bg-violet-500",
      admin: "bg-blue-500",
    };
    return colors[type] || "bg-gray-500";
  };

  const getActivityActionText = (activity) => {
    const actions = {
      added: "added",
      updated: "updated",
      published: "published",
      created: "created",
      received: "received",
      read: "read",
      replied: "replied",
      resolved: "resolved",
      completed: "completed",
      approved: "approved",
      contacted: "contacted",
      quoted: "quoted",
      scheduled: "scheduled",
      archived: "archived",
    };
    return actions[activity.action] || activity.action;
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    gradient,
    trend,
    trendValue,
    subtitle,
    delay = 0,
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 300 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
      />
      <div className="relative p-5">
        <div className="flex items-center justify-between mb-3">
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}
          >
            <Icon size={22} className="text-white" />
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {typeof value === "number" ? value?.toLocaleString() || 0 : value}
            </span>
            {trend !== undefined && trend !== null && (
              <div
                className={`flex items-center justify-end gap-0.5 text-xs mt-1 ${trend >= 0 ? "text-emerald-500" : "text-red-500"}`}
              >
                {trend >= 0 ? (
                  <TrendingUp size={12} />
                ) : (
                  <TrendingDown size={12} />
                )}
                <span>{Math.abs(trend)}%</span>
              </div>
            )}
          </div>
        </div>
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {subtitle}
          </p>
        )}
      </div>
    </motion.div>
  );

  const QuickActionButton = ({ action, onClick, index }) => (
    <motion.button
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.8 + index * 0.05 }}
      whileHover={{ scale: 1.02, x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group flex items-center gap-4 p-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-800/50 hover:shadow-md transition-all duration-300 cursor-pointer"
    >
      <div
        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}
      >
        <action.icon size={22} className="text-white" />
      </div>
      <div className="flex-1 text-left">
        <p className="text-base font-semibold text-gray-900 dark:text-white">
          {action.label}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {action.description}
        </p>
      </div>
      <ChevronRight
        size={18}
        className="text-gray-400 group-hover:translate-x-1 transition-transform"
      />
    </motion.button>
  );

  const quickActions = [
    {
      label: "Write Blog",
      description: "Create new blog post",
      icon: BookOpen,
      gradient: "from-purple-500 to-pink-500",
      path: "/admin/blog",
    },
    {
      label: "View Messages",
      description: "Check unread messages",
      icon: MessageSquare,
      gradient: "from-emerald-500 to-teal-500",
      path: "/admin/messages",
    },
    {
      label: "Add Product",
      description: "Create new product listing",
      icon: Package,
      gradient: "from-blue-500 to-cyan-500",
      path: "/admin/products",
    },
    {
      label: "View Requests",
      description: "Process new customer requests",
      icon: Inbox,
      gradient: "from-amber-500 to-orange-500",
      path: "/admin/requests",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Zap size={28} className="text-primary-400 animate-pulse" />
            </div>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-4 font-medium">
            Loading dashboard...
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Fetching latest data
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="inline-flex p-5 bg-red-100 dark:bg-red-900/20 rounded-full mb-5">
            <AlertCircle size={56} className="text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Unable to Load Dashboard
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
          >
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* iOS-style Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-primary-500/10 via-secondary-500/5 to-transparent rounded-3xl p-6 md:p-8"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary-500/10 rounded-full blur-3xl" />

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">👋</span>
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-3 py-1 rounded-full">
                {greeting}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {currentUser?.firstName || "Admin"} {currentUser?.lastName || ""}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Here's what's happening with your platform today.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-800/50">
              <Calendar size={18} className="text-primary-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currentTime.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-xl border border-primary-200/30 dark:border-primary-800/30">
              <Shield size={16} className="text-primary-500" />
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                {currentUser?.role?.replace("_", " ").toUpperCase() || "ADMIN"}
              </span>
            </div>
            <button
              onClick={fetchDashboardData}
              className="p-2.5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-500" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Total Admins"
          value={stats.admins.total}
          icon={Users}
          gradient="from-blue-500 to-cyan-500"
          trend={10}
          delay={0.1}
          subtitle={`${stats.admins.active} active · ${stats.admins.recentlyActive} recently active`}
        />
        <StatCard
          title="Requests"
          value={stats.requests.total}
          icon={Inbox}
          gradient="from-amber-500 to-orange-500"
          trend={12}
          delay={0.2}
          subtitle={`${stats.requests.pending} pending · ${stats.requests.completed} completed`}
        />
        <StatCard
          title="Messages"
          value={stats.messages.total}
          icon={MessageSquare}
          gradient="from-emerald-500 to-teal-500"
          trend={-5}
          delay={0.3}
          subtitle={`${stats.messages.unread} unread · ${stats.messages.resolved} resolved`}
        />
        <StatCard
          title="Blog Posts"
          value={stats.blogs.total}
          icon={BookOpen}
          gradient="from-purple-500 to-pink-500"
          trend={8}
          delay={0.4}
          subtitle={`${stats.blogs.published} published · ${stats.blogs.drafts} drafts`}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard
          title="Products"
          value={stats.products.total}
          icon={Package}
          gradient="from-pink-500 to-rose-500"
          delay={0.5}
          subtitle={`${stats.products.active} active · ${stats.products.featured} featured`}
        />
        <StatCard
          title="Completion Rate"
          value={`${stats.requests.completionRate || 0}%`}
          icon={Target}
          gradient="from-emerald-500 to-green-500"
          trend={stats.requests.completionRate}
          delay={0.6}
        />
        <StatCard
          title="Resolution Rate"
          value={`${stats.messages.resolvedRate || 0}%`}
          icon={Award}
          gradient="from-teal-500 to-cyan-500"
          trend={stats.messages.resolvedRate}
          delay={0.7}
        />
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity - iOS-style List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-lg overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Recent Activity
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Latest updates across your platform
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            <AnimatePresence>
              {recentActivity.length > 0 ? (
                recentActivity.slice(0, 8).map((activity, index) => {
                  const Icon = getActivityIcon(activity.type);
                  const colorClass = getActivityColor(activity.type);
                  const actionText = getActivityActionText(activity);

                  return (
                    <motion.div
                      key={`${activity.type}-${activity.id}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                      className="flex items-center justify-between p-4 cursor-pointer transition-all"
                      onClick={() => {
                        if (activity.type === "product")
                          navigate(`/admin/products`);
                        if (activity.type === "request")
                          navigate(`/admin/requests`);
                        if (activity.type === "message")
                          navigate(`/admin/messages`);
                        if (activity.type === "blog") navigate(`/admin/blog`);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className={`w-10 h-10 ${colorClass} rounded-xl flex items-center justify-center text-white shadow-sm`}
                        >
                          <Icon size={18} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium capitalize">
                              {activity.type}
                            </span>{" "}
                            {actionText}:{" "}
                            <span className="text-gray-900 dark:text-white font-medium">
                              {activity.item}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                            <Clock size={10} />
                            {formatTimeAgo(activity.time)}
                            {activity.status && (
                              <span
                                className={`px-1.5 py-0.5 rounded-full text-xs ${
                                  activity.status === "completed" ||
                                  activity.status === "published"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : activity.status === "pending"
                                      ? "bg-amber-100 text-amber-700"
                                      : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {activity.status}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-16">
                  <Activity
                    size={56}
                    className="mx-auto mb-4 text-gray-300 dark:text-gray-600"
                  />
                  <p className="text-gray-500 dark:text-gray-400">
                    No recent activity
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Activity will appear here as you use the platform
                  </p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-5"
        >
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Quick Actions
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Common tasks to manage your platform
              </p>
            </div>
            <div className="p-4 space-y-3">
              {quickActions.map((action, index) => (
                <QuickActionButton
                  key={index}
                  action={action}
                  onClick={() => navigate(action.path)}
                  index={index}
                />
              ))}
            </div>
          </div>

          {/* Performance Metrics - iOS-style Card */}
          {Object.keys(performanceMetrics).length > 0 && (
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl">
                    <BarChart3 size={16} className="text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Performance Metrics
                  </h3>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Request Completion</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {performanceMetrics.requestCompletionRate || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${performanceMetrics.requestCompletionRate || 0}%`,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Response Time</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {performanceMetrics.messageResponseTime || 0}h
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(((performanceMetrics.messageResponseTime || 0) / 48) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Resolution Rate</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {performanceMetrics.messageResolutionRate || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${performanceMetrics.messageResolutionRate || 0}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Performance Overview Banner - iOS-style */}
      {(stats.requests.thisMonth > 0 || stats.messages.thisMonth > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="relative overflow-hidden bg-gradient-to-r from-primary-500/10 via-secondary-500/5 to-transparent rounded-2xl p-6 border border-primary-200/30 dark:border-primary-800/30"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full blur-2xl" />
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl shadow-md">
              <Rocket size={20} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              This Month's Performance
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <p className="text-sm text-gray-500">New Requests</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.requests.thisMonth || 0}
              </p>
              <p className="text-xs text-emerald-600">+12% from last month</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Messages</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.messages.thisMonth || 0}
              </p>
              <p className="text-xs text-emerald-600">+8% from last month</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Blog Views</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.blogs.totalViews?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-emerald-600">Total all time</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Product Views</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.products.totalViews?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-emerald-600">Total all time</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;
